export interface SelectionRange {
  start: number;
  end: number;
}

export interface FormatResult {
  newText: string;
  newCursorEnd: number;
  newSelectionStart?: number;
}

export interface MarkdownLinkMatch {
  label: string;
  url: string;
  linkStart: number;
  linkEnd: number;
  labelStart: number;
  labelEnd: number;
}

const LIST_PREFIX_INLINE_RE = /^(\s*(?:(?:[–—\-*•]\s+)?\[[ xX]?\]\s+|[–—\-*•]\s+|\d+[.)]\s+)?)/;

const TEMPO_UNIT_SYMBOLS: Record<string, string> = {
  half: "𝅗𝅥",
  quarter: "♩",
  eighth: "♪",
  sixteenth: "♬",
  "dotted-quarter": "♩.",
};

/**
  * Checks whether a URL is a Score app URL (`score.practice-mate.app` or a URL carrying score loop/bookmark params).
  */
export function isScoreAppUrl(urlStr: string): boolean {
  if (!urlStr) return false;
  try {
    const parsed = new URL(urlStr.trim());
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return false;
    const host = parsed.hostname.toLowerCase();
    if (host === "score.practice-mate.app" || host.startsWith("score.")) {
      return true;
    }
    const params = parsed.searchParams;
    if (
      params.has("loopStartBeat") ||
      params.has("loopEndBeat") ||
      params.has("loopStartM") ||
      params.has("loopEndM") ||
      (params.has("driveId") && (params.has("page") || params.has("format")))
    ) {
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

/**
  * Extracts anchor text from clipboard HTML if it contains `<a href="...">label</a>`.
  */
export function extractClipboardAnchorTitle(
  clipboardHtml?: string | null,
  expectedUrl?: string
): string | null {
  if (!clipboardHtml) return null;
  const match = /<a\b[^>]*href=["']([^"']+)["'][^>]*>([\s\S]*?)<\/a>/i.exec(clipboardHtml);
  if (!match) return null;
  const rawLabel = match[2]
    .replace(/<[^>]+>/g, "")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .trim();
  if (!rawLabel || rawLabel === expectedUrl || /^https?:\/\//i.test(rawLabel)) {
    return null;
  }
  return rawLabel;
}

/**
  * Generates a rich human-readable label for a Score app loop, bookmark, or score URL.
  * Examples:
  * - Loop with measures & tempo: `"m. 9–11 • ♪ 60 BPM"`
  * - Loop with custom label & tempo: `"Arpeggio • ♪ 60 BPM"`
  * - Loop with beats only: `"Beat 32–41 • ♪ 60 BPM"`
  * - Bookmark with page: `"Page 1"` (or custom label if provided)
  * - Score link without page/loop: `"Carcassi - Op 60, No 1"`
  */
export function getScoreAppLinkLabel(
  urlStr: string,
  clipboardHtml?: string | null
): string | null {
  if (!isScoreAppUrl(urlStr)) return null;
  try {
    const parsed = new URL(urlStr.trim());
    const params = parsed.searchParams;

    const htmlTitle = extractClipboardAnchorTitle(clipboardHtml, urlStr.trim());
    const labelParam =
      params.get("label")?.trim() ||
      params.get("bookmark")?.trim() ||
      params.get("bm")?.trim() ||
      params.get("title")?.trim();

    const loopStartM = params.get("loopStartM")?.trim();
    const loopEndM = params.get("loopEndM")?.trim();
    const loopStartBeat = params.get("loopStartBeat")?.trim();
    const loopEndBeat = params.get("loopEndBeat")?.trim();
    const bpm = params.get("bpm")?.trim();
    const tempoUnit = params.get("tempoUnit")?.trim();
    const page = params.get("page")?.trim();
    const name = params.get("name")?.trim();

    const isLoop = Boolean(loopStartM || loopEndM || loopStartBeat || loopEndBeat);

    let baseTitle = "";
    if (labelParam) {
      baseTitle = labelParam;
    } else if (htmlTitle) {
      baseTitle = htmlTitle;
    } else if (isLoop) {
      if (loopStartM && loopEndM) {
        baseTitle = loopStartM === loopEndM ? `m. ${loopStartM}` : `m. ${loopStartM}–${loopEndM}`;
      } else if (loopStartM) {
        baseTitle = `m. ${loopStartM}`;
      } else if (loopStartBeat && loopEndBeat) {
        const sb = Math.round(Number(loopStartBeat));
        const eb = Math.round(Number(loopEndBeat));
        if (!isNaN(sb) && !isNaN(eb)) {
          baseTitle = `Beat ${sb}–${eb}`;
        }
      }
    } else if (page) {
      baseTitle = `Page ${page}`;
    } else if (name) {
      baseTitle = name.replace(/\.(musicxml|mxl|xml|pdf)$/i, "");
    }

    if (bpm && !isNaN(Number(bpm)) && Number(bpm) > 0) {
      const symbol = tempoUnit ? TEMPO_UNIT_SYMBOLS[tempoUnit] : undefined;
      const tempoStr = `${symbol ? `${symbol} ` : ""}${bpm} BPM`;
      if (!baseTitle) {
        baseTitle = tempoStr;
      } else if (!/\bbpm\b/i.test(baseTitle)) {
        baseTitle = `${baseTitle} • ${tempoStr}`;
      }
    }

    return baseTitle || null;
  } catch {
    return null;
  }
}

/**
  * Returns the best display label for a pasted URL (checking Score app URL params and clipboard HTML),
  * or falls back to the raw URL if no rich label can be derived.
  */
export function getPastedUrlLabel(
  urlStr: string,
  clipboardHtml?: string | null
): string {
  const scoreLabel = getScoreAppLinkLabel(urlStr, clipboardHtml);
  if (scoreLabel) return scoreLabel;
  const htmlLabel = extractClipboardAnchorTitle(clipboardHtml, urlStr.trim());
  if (htmlLabel) return htmlLabel;
  return urlStr.trim();
}

/**
  * Formats a label and URL as a Markdown link `[label](url)`, escaping any brackets in the label.
  */
export function formatMarkdownLink(label: string, url: string): string {
  const safeLabel = (label || url).replace(/\[/g, "(").replace(/\]/g, ")");
  return `[${safeLabel}](${url.trim()})`;
}

/**
 * Finds an existing markdown link `[label](url)` enclosing or matching `selection`.
 */
export function getLinkAtSelection(
  text: string,
  selection: SelectionRange | null
): MarkdownLinkMatch | null {
  if (!selection || !text) return null;

  // Trim leading/trailing whitespace and list prefix from selection range if non-collapsed
  let selStart = selection.start;
  let selEnd = selection.end;
  if (selStart < selEnd) {
    const raw = text.slice(selStart, selEnd);
    const leadMatch = raw.match(LIST_PREFIX_INLINE_RE);
    const trailMatch = raw.match(/(\s*)$/);
    const leadLen = leadMatch ? leadMatch[1].length : 0;
    const trailLen = trailMatch ? trailMatch[1].length : 0;
    if (selStart + leadLen < selEnd - trailLen) {
      selStart += leadLen;
      selEnd -= trailLen;
    }
  }

  const re = /\[([^\]]+)\]\(([^)]+)\)/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    const linkStart = match.index;
    const linkEnd = linkStart + match[0].length;
    const labelStart = linkStart + 1;
    const labelEnd = labelStart + match[1].length;

    if (selStart >= linkStart && selEnd <= linkEnd) {
      return {
        label: match[1],
        url: match[2],
        linkStart,
        linkEnd,
        labelStart,
        labelEnd,
      };
    }
  }
  return null;
}

/**
 * Unwraps `[label](url)` at `selection` back into plain `label`.
 */
export function removeLinkAtSelection(
  text: string,
  selection: SelectionRange | null
): FormatResult | null {
  const existing = getLinkAtSelection(text, selection);
  if (!existing) return null;
  const newText =
    text.slice(0, existing.linkStart) +
    existing.label +
    text.slice(existing.linkEnd);
  return {
    newText,
    newSelectionStart: existing.linkStart,
    newCursorEnd: existing.linkStart + existing.label.length,
  };
}

/**
 * Applies Bold (**text**), Italic (*text*), or Markdown Link ([text](url)) formatting to a selection.
 * If text is already formatted as bold or italic, toggles the formatting off.
 */
export function applyTextFormat(
  text: string,
  selection: SelectionRange,
  action: "bold" | "italic" | "link",
  url?: string,
  opts?: { linkText?: string }
): FormatResult | null {
  if (selection.start === selection.end) return null;
  const rawSel = opts?.linkText ?? text.slice(selection.start, selection.end);
  if (!rawSel) return null;

  if (action === "link") {
    if (!url) return null;
    const existingLink = getLinkAtSelection(text, selection);
    if (existingLink) {
      const nextLabel = opts?.linkText ?? existingLink.label;
      const replacement = `[${nextLabel}](${url})`;
      const newText =
        text.slice(0, existingLink.linkStart) +
        replacement +
        text.slice(existingLink.linkEnd);
      return {
        newText,
        newSelectionStart: existingLink.linkStart,
        newCursorEnd: existingLink.linkStart + replacement.length,
      };
    }
    if (opts?.linkText !== undefined) {
      const newText = text.slice(0, selection.start) + `[${rawSel}](${url})` + text.slice(selection.end);
      const newCursorEnd = selection.start + rawSel.length + url.length + 4;
      return { newText, newCursorEnd, newSelectionStart: selection.start };
    }

    // If selection is directly inside **...** or *...*, include those delimiters inside the link label
    // so the result is `[**text**](url)` rather than `**[text](url)**`
    let linkSelStart = selection.start;
    let linkSelEnd = selection.end;
    if (
      linkSelStart >= 2 &&
      text.slice(linkSelStart - 2, linkSelStart) === "**" &&
      text.slice(linkSelEnd, linkSelEnd + 2) === "**"
    ) {
      linkSelStart -= 2;
      linkSelEnd += 2;
    } else if (
      linkSelStart >= 1 &&
      text.slice(linkSelStart - 1, linkSelStart) === "*" &&
      text.slice(linkSelEnd, linkSelEnd + 1) === "*"
    ) {
      linkSelStart -= 1;
      linkSelEnd += 1;
    }
    const effectiveSel = text.slice(linkSelStart, linkSelEnd);

    // Separate leading and trailing whitespace so link brackets never trap newlines or outer spaces
    const leadMatch = effectiveSel.match(/^(\s*)/);
    const trailMatch = effectiveSel.match(/(\s*)$/);
    const lead = leadMatch ? leadMatch[1] : "";
    const trail = trailMatch ? trailMatch[1] : "";
    const core = effectiveSel.slice(lead.length, effectiveSel.length - trail.length);

    if (core.length === 0) return null;

    // Multiline selection: wrap each non-empty line individually, keeping list prefixes outside [...]
    if (core.includes("\n")) {
      const lines = core.split("\n");
      const newLines = lines.map((line) => {
        if (line.trim().length === 0) return line;
        const prefixMatch = line.match(LIST_PREFIX_INLINE_RE);
        const lLead = prefixMatch ? prefixMatch[1] : "";
        const rest = line.slice(lLead.length);
        const lTrailMatch = rest.match(/(\s*)$/);
        const lTrail = lTrailMatch ? lTrailMatch[1] : "";
        const lCore = rest.slice(0, rest.length - lTrail.length);
        if (!lCore) return line;
        return `${lLead}[${lCore}](${url})${lTrail}`;
      });

      const newCore = newLines.join("\n");
      const newText = text.slice(0, linkSelStart) + lead + newCore + trail + text.slice(linkSelEnd);
      const newSelectionStart = linkSelStart;
      const newCursorEnd = linkSelStart + lead.length + newCore.length + trail.length;
      return { newText, newCursorEnd, newSelectionStart };
    }

    // Single-line selection: keep list prefix (-, *, •, 1., [ ], etc.) outside [...]
    const prefixMatch = core.match(LIST_PREFIX_INLINE_RE);
    const pLead = prefixMatch ? prefixMatch[1] : "";
    const actualCore = core.slice(pLead.length);
    if (!actualCore) return null;
    const replaced = lead + pLead + `[${actualCore}](${url})` + trail;
    const newText = text.slice(0, linkSelStart) + replaced + text.slice(linkSelEnd);
    const newSelectionStart = linkSelStart;
    const newCursorEnd = linkSelStart + replaced.length;
    return { newText, newCursorEnd, newSelectionStart };
  }

  const delim = action === "bold" ? "**" : "*";
  const delimLen = delim.length;

  // Separate leading and trailing whitespace so delimiters never trap newlines or extra spaces
  const leadMatch = rawSel.match(/^(\s*)/);
  const trailMatch = rawSel.match(/(\s*)$/);
  const lead = leadMatch ? leadMatch[1] : "";
  const trail = trailMatch ? trailMatch[1] : "";
  const core = rawSel.slice(lead.length, rawSel.length - trail.length);

  if (core.length === 0) return null;

  // 1. Check if selection is directly surrounded by delimiters in the outer text
  // e.g. text is "**world**" and selection is "world"
  if (
    selection.start >= delimLen &&
    text.slice(selection.start - delimLen, selection.start) === delim &&
    text.slice(selection.end, selection.end + delimLen) === delim
  ) {
    const isDoubleAsterisk =
      delim === "*" &&
      ((selection.start >= 2 && text.slice(selection.start - 2, selection.start) === "**") ||
        text.slice(selection.end, selection.end + 2) === "**");

    if (!isDoubleAsterisk) {
      const newText = text.slice(0, selection.start - delimLen) + rawSel + text.slice(selection.end + delimLen);
      const newSelectionStart = selection.start - delimLen;
      const newCursorEnd = newSelectionStart + rawSel.length;
      return { newText, newCursorEnd, newSelectionStart };
    }
  }

  // 2. Check if the selection itself contains the delimiter (e.g. user selected "**text**" or "line 1 **text**")
  // In this case, clicking the format button un-formats / strips the delimiters!
  if (action === "bold" && core.includes("**")) {
    const strippedCore = core.replace(/\*\*/g, "");
    const replaced = lead + strippedCore + trail;
    const newText = text.slice(0, selection.start) + replaced + text.slice(selection.end);
    const newSelectionStart = selection.start;
    const newCursorEnd = selection.start + replaced.length;
    return { newText, newCursorEnd, newSelectionStart };
  }

  if (action === "italic" && /(?<!\*)\*(?!\*)/.test(core)) {
    // Strip single asterisks (without stripping **)
    const strippedCore = core.replace(/(?<!\*)\*(?!\*)/g, "");
    const replaced = lead + strippedCore + trail;
    const newText = text.slice(0, selection.start) + replaced + text.slice(selection.end);
    const newSelectionStart = selection.start;
    const newCursorEnd = selection.start + replaced.length;
    return { newText, newCursorEnd, newSelectionStart };
  }

  // 3. Multiline formatting (neither surrounded nor contains delimiter):
  // Format each non-empty line individually so delimiters never cross newlines.
  // Keep any list prefixes (-, *, •, –, —, 1., [ ], etc.) outside the delimiters!
  if (core.includes("\n")) {
    const lines = core.split("\n");
    const newLines = lines.map((line) => {
      if (line.trim().length === 0) return line;
      const prefixMatch = line.match(LIST_PREFIX_INLINE_RE);
      const lLead = prefixMatch ? prefixMatch[1] : "";
      const rest = line.slice(lLead.length);
      const lTrailMatch = rest.match(/(\s*)$/);
      const lTrail = lTrailMatch ? lTrailMatch[1] : "";
      const lCore = rest.slice(0, rest.length - lTrail.length);
      return `${lLead}${delim}${lCore}${delim}${lTrail}`;
    });

    const newCore = newLines.join("\n");
    const newText = text.slice(0, selection.start) + lead + newCore + trail + text.slice(selection.end);
    const newSelectionStart = selection.start;
    const newCursorEnd = selection.start + lead.length + newCore.length + trail.length;
    return { newText, newCursorEnd, newSelectionStart };
  }

  // 4. Single line formatting (wrap in delimiter, keeping list prefixes outside delim)
  const prefixMatch = core.match(LIST_PREFIX_INLINE_RE);
  const pLead = prefixMatch ? prefixMatch[1] : "";
  const actualCore = core.slice(pLead.length);
  const replaced = lead + pLead + `${delim}${actualCore}${delim}` + trail;
  const newText = text.slice(0, selection.start) + replaced + text.slice(selection.end);
  const newSelectionStart = selection.start;
  const newCursorEnd = selection.start + replaced.length;
  return { newText, newCursorEnd, newSelectionStart };
}

/**
 * Toggles or applies bulleted list (- ), numbered list (1. ), or to-do list ([ ] ) formatting to lines touched by selection.
 */
export function applyListFormat(
  text: string,
  selection: SelectionRange,
  type: "bullet" | "number" | "todo"
): FormatResult {
  if (text.length === 0) {
    const prefix = type === "bullet" ? "- " : type === "number" ? "1. " : "[ ] ";
    return {
      newText: prefix,
      newCursorEnd: prefix.length,
      newSelectionStart: prefix.length,
    };
  }

  // Find start of the first line touched by selection
  let lineStart = text.lastIndexOf("\n", selection.start - 1);
  lineStart = lineStart === -1 ? 0 : lineStart + 1;

  // Find end of the last line touched by selection
  let selEnd = selection.end;
  if (selEnd > selection.start && text[selEnd - 1] === "\n") {
    selEnd = selEnd - 1;
  }
  let lineEnd = text.indexOf("\n", selEnd);
  lineEnd = lineEnd === -1 ? text.length : lineEnd;

  const targetRangeText = text.slice(lineStart, lineEnd);
  const lines = targetRangeText.split("\n");

  const todoRegex = /^(\s*)(\*{0,2})((?:[–—\-*•]\s+)?\[[ xX]?\]\s+)/;
  const bulletRegex = /^(\s*)(\*{0,2})([–—\-*•]\s+)/;
  const numberRegex = /^(\s*)(\*{0,2})(\d+[.)]\s+)/;

  // Check if all non-empty lines already have the target format
  const nonEmptyLines = lines.filter((l) => l.trim().length > 0);
  const allAlreadyTarget =
    nonEmptyLines.length > 0 &&
    nonEmptyLines.every((l) => {
      if (type === "todo") return todoRegex.test(l);
      if (type === "bullet") return bulletRegex.test(l) && !todoRegex.test(l);
      if (type === "number") return numberRegex.test(l);
      return false;
    });

  let numCounter = 1;
  const newLines = lines.map((line) => {
    // If multiline selection and line is empty, preserve empty line
    if (line.trim().length === 0 && lines.length > 1) {
      return line;
    }

    if (allAlreadyTarget) {
      // Toggle off: remove the list prefix
      if (type === "todo") {
        return line.replace(todoRegex, "$1$2");
      } else if (type === "bullet") {
        return line.replace(bulletRegex, "$1$2");
      } else {
        return line.replace(numberRegex, "$1$2");
      }
    } else {
      // Strip any existing todo, bullet, or number prefix first
      const stripped = line
        .replace(todoRegex, "$1$2")
        .replace(bulletRegex, "$1$2")
        .replace(numberRegex, "$1$2");
      const indentMatch = stripped.match(/^(\s*)/);
      const indent = indentMatch ? indentMatch[1] : "";
      const content = stripped.slice(indent.length);

      if (type === "todo") {
        return `${indent}[ ] ${content}`;
      } else if (type === "bullet") {
        return `${indent}- ${content}`;
      } else {
        const prefix = `${indent}${numCounter}. `;
        numCounter++;
        return `${prefix}${content}`;
      }
    }
  });

  const replacedText = newLines.join("\n");
  const newText = text.slice(0, lineStart) + replacedText + text.slice(lineEnd);
  const newCursorEnd = lineStart + replacedText.length;
  const newSelectionStart = lineStart;

  return { newText, newCursorEnd, newSelectionStart };
}

/**
 * Strips markdown link formatting from a string, returning only the text label.
 * Also handles partially mangled link markup gracefully.
 */
export function stripMarkdownLinks(str: string | null | undefined): string {
  if (!str) return "";
  return str
    .replace(/\[([^\]]+)\]\((?:https?:\/\/[^\s\)]+|[^)]+)\)/g, "$1")
    .replace(/\[([^\]\(\)]+)\]?\((https?:\/\/[^\s\)]*)\)?/g, "$1");
}

/**
 * Resets all checked markdown to-do items ([x] or [X]) back to unchecked ([ ])
 * across all lines of a markdown string (such as segmentGoal).
 */
export function resetMarkdownTodos(text: string): string {
  if (!text) return text;
  const lines = text.split("\n");
  const updated = lines.map((line) =>
    line.replace(
      /^(\s*(?:\[\s*)?(?:\*{1,2})?(?:[-*•–—]\s+)?(?:\*{1,2})?\[)[xX](\])/,
      "$1 $2"
    )
  );
  return updated.join("\n");
}

